import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { endpoints } from 'Core/defaultValues';
import Loading from 'Components/Loading';

import { useCoachingToken } from './hooks/useCoachingToken';
import { useCoachingMessages } from './hooks/useCoachingMessages';
import { useCoachingReachability } from './hooks/useCoachingReachability';
import { useIframeLoadStatus } from './hooks/useIframeLoadStatus';
import { useCoachingTab } from './hooks/useCoachingTab';
import { useCoachingNavigation } from './hooks/useCoachingNavigation';
import { errorCodeToMessageKey } from './api/coachingTokenError';
import CoachingFrame from './components/CoachingFrame';
import CoachingTabs from './components/CoachingTabs';
import CoachingErrorState from './components/CoachingErrorState';
import { hasCoachingAccess, getAllowedTabs } from './permissions';
import { DEFAULT_COACHING_TAB } from './constants';
import './coachSSO.scss';

// Coaching integration: fetch an SSO token, then embed the Coaching app with it.
// A header of tabs navigates inside the embedded app through postMessage.
const CoachSSOView = () => {
  const navigate = useNavigate();
  const { messages } = useIntl();
  const user = useSelector((state) => state.securityRedux.user);
  const allowed = hasCoachingAccess(user);
  // Header tabs the user's Coaching role may open (see permissions.js)
  const tabs = useMemo(() => getAllowedTabs(user), [user]);

  useEffect(() => {
    if (user && !allowed) {
      navigate('/error');
    }
  }, [user, allowed, navigate]);

  const { token, isLoading, error } = useCoachingToken({ enabled: allowed });
  const host = useCoachingReachability({ enabled: allowed });
  useCoachingMessages();

  const src = token ? `${endpoints.COACHING_URL}/sso?access_token=${encodeURIComponent(token)}` : null;
  const frame = useIframeLoadStatus({ src });

  const frameRef = useRef(null);
  const { activeTab, activeIndex, selectTab } = useCoachingTab({ enabled: allowed, tabs });
  const { navigateTo } = useCoachingNavigation({ frameRef });

  // Single place that talks to the Coaching app: every active-tab change (click, restored tab,
  // browser back/forward) is sent once the iframe has loaded. Messages are never sent to a frame
  // that is still loading; the tab current at `load` time is sent then. The app opens on the
  // default tab by itself, so that one needs no message right after a load.
  const sentTabRef = useRef(null);
  useEffect(() => {
    if (!frame.isLoaded) {
      sentTabRef.current = null;
      return;
    }
    if (sentTabRef.current === activeTab) return;

    const firstAfterLoad = sentTabRef.current === null;
    sentTabRef.current = activeTab;
    if (firstAfterLoad && activeTab.path === DEFAULT_COACHING_TAB.path) return;

    navigateTo(activeTab.path);
  }, [frame.isLoaded, activeTab, navigateTo]);

  if (error) {
    return <CoachingErrorState messageKey={errorCodeToMessageKey(error.code)} />;
  }

  // The host did not answer (DNS failure, connection refused) or the frame ended on a blank document.
  if (host.isUnreachable || frame.isFailed) {
    return <CoachingErrorState messageKey="coachSSO_error_frameLoad" />;
  }

  if (isLoading || host.isChecking || !src) {
    return <Loading className="center-loading" />;
  }

  return (
    <div className="coach-sso-view">
      <CoachingTabs tabs={tabs} activeIndex={activeIndex} onChange={selectTab} />
      <CoachingFrame
        src={src}
        title={messages['coachSSO_iframeTitle']}
        isLoaded={frame.isLoaded}
        onLoad={frame.onLoad}
        frameRef={frameRef}
      />
    </div>
  );
};

export default CoachSSOView;
